import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
    color_colors_gold,
    color_colors_gold_background,
    color_colors_light_ocean,
    color_colors_ocean,
    color_defaults_white,
    color_shades_dark,
    color_shades_darker,
    color_shades_darkest,
    color_shades_lighter,
    dark_grey_blue
} from '../../constants/colors';
import SelectField from '../forms/SelectField';
import { allKnownVoiceOptions, SparkVoiceOption, voiceOptions } from '../../constants/sparkConstants/voiceOptions';
import { defaultRimeVoice, defaultVoice } from '../../constants/sparkConstants/voices';
import {
    carryVoiceStringPrompt,
    getVoiceStringPrompt,
    setVoiceStringPrompt,
    supportsVoicePrompt,
    voiceStringsMatch
} from '../../utils/ttsVoiceString';

export interface TtsSelection {
    ttsProvider: string
    ttsVoice: string
}

interface TelephonyVoicePickerProps {
    value: TtsSelection
    onChange: (value: TtsSelection, option?: SparkVoiceOption) => void
    /** Picks the Spanish voice string for es-* locales. */
    locale?: string
    /** Shows a Custom card for entering any provider and voice string. */
    allowCustom?: boolean
    /** Shows the raw voice string under the cards. */
    showVoiceString?: boolean
    disabled?: boolean
    idPrefix?: string
}

const CUSTOM_PROVIDER_EXAMPLES = [
    { provider: "ElevenLabs", voice: defaultVoice },
    { provider: "Gemini", voice: "voice_name=Puck|model=gemini-3.8-flash-tts|speed=1.05" },
    { provider: "Rime", voice: defaultRimeVoice },
    { provider: "Google", voice: "en-US-Neural2-F" }
];

const PROVIDER_CHOICES = ["ElevenLabs", "Gemini", "Rime", "Google", "Inworld", "Soniox", "DeepgramFlux", "Polly", "Microsoft"];

/** Known providers, plus the saved one when it isn't in the list. */
const getProviderOptions = (currentProvider?: string) => {
    const choices = currentProvider && !PROVIDER_CHOICES.some(choice => providersMatch(choice, currentProvider))
        ? [currentProvider, ...PROVIDER_CHOICES]
        : PROVIDER_CHOICES;
    return choices.map(choice => ({ label: choice, value: choice }));
};

const isSpanishLocale = (locale?: string) => (locale || "").toLowerCase().startsWith("es");

const getOptionVoiceString = (option: SparkVoiceOption, locale?: string) =>
    isSpanishLocale(locale) ? option.spanishVoice : option.englishVoice;

const providersMatch = (left?: string, right?: string) =>
    !left || !right || left.trim().toLowerCase() === right.trim().toLowerCase();

export const findVoiceOptionForSelection = (value: TtsSelection, locale?: string): SparkVoiceOption | undefined => {
    if (!value?.ttsVoice) return undefined;
    const matches = (option: SparkVoiceOption, voice: string) =>
        providersMatch(option.provider, value.ttsProvider) && voiceStringsMatch(voice, value.ttsVoice);

    return allKnownVoiceOptions.find(option => matches(option, getOptionVoiceString(option, locale)))
        || allKnownVoiceOptions.find(option => matches(option, option.englishVoice) || matches(option, option.spanishVoice));
};

const TelephonyVoicePicker: React.FC<TelephonyVoicePickerProps> = (props) => {
    const idPrefix = props.idPrefix || "telephony-voice";
    const value = props.value || { ttsProvider: "", ttsVoice: "" };
    const currentOption = findVoiceOptionForSelection(value, props.locale);
    const hasValue = !!(value.ttsProvider || value.ttsVoice);

    const [isCustom, setIsCustom] = useState(!!props.allowCustom && hasValue && !currentOption);
    const [playingSrc, setPlayingSrc] = useState<string>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const lastEmittedRef = useRef<TtsSelection>(null);

    const emitChange = (selection: TtsSelection, option?: SparkVoiceOption) => {
        lastEmittedRef.current = selection;
        props.onChange(selection, option);
    };

    // Custom mode is local state, so re-derive it when the selection is changed from outside
    // (e.g. a voice pool row is removed or another locale is shown).
    useEffect(() => {
        const lastEmitted = lastEmittedRef.current;
        if (lastEmitted && lastEmitted.ttsProvider === value.ttsProvider && lastEmitted.ttsVoice === value.ttsVoice) return;
        setIsCustom(!!props.allowCustom && hasValue && !currentOption);
    }, [value.ttsProvider, value.ttsVoice]);

    useEffect(() => () => audioRef.current?.pause(), []);

    const prompt = getVoiceStringPrompt(value.ttsVoice);
    const showPromptEditor = supportsVoicePrompt(value.ttsProvider) && (isCustom || currentOption?.tier === "promptable");
    const customExample = CUSTOM_PROVIDER_EXAMPLES.find(example => providersMatch(example.provider, value.ttsProvider) && !!value.ttsProvider);

    const selectOption = (option: SparkVoiceOption) => {
        if (props.disabled) return;
        setIsCustom(false);
        const keepPrompt = supportsVoicePrompt(option.provider) && supportsVoicePrompt(value.ttsProvider) ? prompt : "";
        emitChange({
            ttsProvider: option.provider,
            ttsVoice: carryVoiceStringPrompt(getOptionVoiceString(option, props.locale), keepPrompt)
        }, option);
    };

    const selectCustom = () => {
        if (props.disabled) return;
        setIsCustom(true);
    };

    const togglePreview = (event: React.MouseEvent, src: string) => {
        event.stopPropagation();
        if (playingSrc === src) {
            audioRef.current?.pause();
            setPlayingSrc(null);
            return;
        }
        audioRef.current?.pause();
        const audio = new Audio(src);
        audio.onended = () => setPlayingSrc(null);
        audioRef.current = audio;
        audio.play().catch(() => setPlayingSrc(null));
        setPlayingSrc(src);
    };

    const renderCard = (option: SparkVoiceOption) => {
        const isSelected = !isCustom && currentOption?.value === option.value;
        return (
            <VoiceCard
                key={option.value}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={isSelected ? "selected" : ""}
                disabled={props.disabled}
                onClick={() => selectOption(option)}
            >
                <CardTop>
                    <RadioDot className={isSelected ? "selected" : ""} />
                    <VoiceName>{option.label}</VoiceName>
                    {option.isDefault && <Badge>Default</Badge>}
                    {option.tier === "promptable" && <Badge className="promptable">Promptable</Badge>}
                </CardTop>
                <CardBottom>
                    <VoiceDetail>{option.detail}</VoiceDetail>
                    {option.audioSrc && (
                        <PreviewButton
                            role="button"
                            tabIndex={0}
                            aria-label={`${playingSrc === option.audioSrc ? "Stop" : "Play"} ${option.label} sample`}
                            onClick={(event) => togglePreview(event, option.audioSrc)}
                        >
                            {playingSrc === option.audioSrc ? "■ Stop" : "▶ Listen"}
                        </PreviewButton>
                    )}
                </CardBottom>
            </VoiceCard>
        );
    };

    const standardOptions = voiceOptions.filter(option => option.tier === "standard");
    const promptableOptions = voiceOptions.filter(option => option.tier === "promptable");

    return (
        <PickerWrapper>
            {!isCustom && currentOption?.tier === "legacy" && (
                <Notice>
                    This connection uses <strong>{currentOption.label}</strong>, which is no longer offered. It keeps working until you pick a new voice.
                </Notice>
            )}
            {!isCustom && hasValue && !currentOption && !props.allowCustom && (
                <Notice>This connection uses a custom voice. It keeps working until you pick a new voice.</Notice>
            )}

            <GroupLabel>Standard voices</GroupLabel>
            <CardGrid role="radiogroup" aria-label="Standard voices">
                {standardOptions.map(renderCard)}
            </CardGrid>

            <GroupLabel>Promptable voices</GroupLabel>
            <CardGrid role="radiogroup" aria-label="Promptable voices">
                {promptableOptions.map(renderCard)}
                {props.allowCustom && (
                    <VoiceCard
                        type="button"
                        role="radio"
                        aria-checked={isCustom}
                        className={isCustom ? "selected" : ""}
                        disabled={props.disabled}
                        onClick={selectCustom}
                    >
                        <CardTop>
                            <RadioDot className={isCustom ? "selected" : ""} />
                            <VoiceName>Custom</VoiceName>
                        </CardTop>
                        <CardBottom>
                            <VoiceDetail>Any provider and voice string</VoiceDetail>
                        </CardBottom>
                    </VoiceCard>
                )}
            </CardGrid>

            {isCustom && (
                <FieldRow>
                    <FieldGroup className="provider">
                        <SelectField
                            id={`${idPrefix}-provider`}
                            label="Provider"
                            useForm={false}
                            useFormTheme={true}
                            clearable={false}
                            disabled={props.disabled}
                            placeholder="Select provider"
                            options={getProviderOptions(value.ttsProvider)}
                            value={value.ttsProvider || ""}
                            onChange={(option) => emitChange({ ...value, ttsProvider: option?.value || "" })}
                        />
                    </FieldGroup>
                    <FieldGroup>
                        <FieldLabel htmlFor={`${idPrefix}-voice`}>Voice string</FieldLabel>
                        <TextArea
                            id={`${idPrefix}-voice`}
                            className="mono"
                            rows={2}
                            value={value.ttsVoice || ""}
                            disabled={props.disabled}
                            placeholder={customExample?.voice || "Enter the provider-specific voice string"}
                            onChange={(event) => emitChange({ ...value, ttsVoice: event.target.value })}
                        />
                    </FieldGroup>
                </FieldRow>
            )}

            {showPromptEditor && (
                <FieldGroup>
                    <FieldLabel htmlFor={`${idPrefix}-prompt`}>Style prompt (optional)</FieldLabel>
                    <TextArea
                        id={`${idPrefix}-prompt`}
                        rows={2}
                        value={prompt}
                        disabled={props.disabled}
                        placeholder="Warm, upbeat and conversational. Smile while you talk."
                        onChange={(event) => emitChange({ ...value, ttsVoice: setVoiceStringPrompt(value.ttsVoice, event.target.value) }, currentOption)}
                    />
                    <HelpText>Tells Gemini how to speak. It's saved in the voice string, so line breaks and | become spaces and double quotes become single quotes.</HelpText>
                </FieldGroup>
            )}

            {props.showVoiceString && !isCustom && value.ttsVoice && (
                <VoiceStringPreview>
                    <span>{value.ttsProvider}</span>
                    <code>{value.ttsVoice}</code>
                </VoiceStringPreview>
            )}
        </PickerWrapper>
    );
};

const PickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    font-family: Muli;
`;

const GroupLabel = styled.span`
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${color_shades_darker};
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
    gap: 8px;
`;

const VoiceCard = styled.button`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-height: 64px;
    padding: 10px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    background: ${color_defaults_white};
    text-align: left;
    font-family: Muli;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
    &:hover:not(:disabled) {
        border-color: ${color_colors_ocean};
    }
    &:focus-visible {
        outline: 2px solid ${color_colors_ocean};
        outline-offset: 1px;
    }
    &.selected {
        border-color: ${color_colors_ocean};
        background: ${color_colors_light_ocean};
        box-shadow: 0 0 0 1px ${color_colors_ocean};
    }
    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const CardTop = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CardBottom = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-left: 24px;
`;

const RadioDot = styled.span`
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid ${color_shades_dark};
    background: ${color_defaults_white};
    box-sizing: border-box;
    &.selected {
        border: 5px solid ${color_colors_ocean};
    }
`;

const VoiceName = styled.span`
    flex: 1;
    font-size: 14px;
    font-weight: bold;
    color: ${dark_grey_blue};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Badge = styled.span`
    padding: 1px 6px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: bold;
    color: ${color_colors_ocean};
    background: ${color_colors_light_ocean};
    &.promptable {
        color: ${color_colors_gold};
        background: ${color_colors_gold_background};
    }
`;

const VoiceDetail = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
`;

const PreviewButton = styled.span`
    font-size: 11px;
    font-weight: bold;
    color: ${color_colors_ocean};
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const Notice = styled.div`
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 13px;
    color: ${color_shades_darkest};
    background: ${color_colors_gold_background};
`;

const FieldRow = styled.div`
    display: grid;
    grid-template-columns: minmax(140px, 200px) 1fr;
    gap: 12px;
    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const FieldLabel = styled.label`
    font-size: 13px;
    font-weight: bold;
    color: ${dark_grey_blue};
`;

const inputStyles = `
    width: 100%;
    box-sizing: border-box;
    padding: 8px 10px;
    border: 1px solid ${color_shades_dark};
    border-radius: 6px;
    font-family: Muli;
    font-size: 13px;
    color: ${dark_grey_blue};
    background: ${color_defaults_white};
    &:focus {
        outline: none;
        border-color: ${color_colors_ocean};
    }
`;

const TextArea = styled.textarea`
    ${inputStyles}
    resize: vertical;
    &.mono {
        font-family: monospace;
        font-size: 12px;
    }
`;

const HelpText = styled.span`
    font-size: 12px;
    color: ${color_shades_darker};
`;

const VoiceStringPreview = styled.div`
    display: flex;
    gap: 8px;
    align-items: baseline;
    padding: 8px 10px;
    border-radius: 6px;
    background: ${color_shades_lighter};
    font-size: 12px;
    color: ${color_shades_darker};
    span {
        font-weight: bold;
        white-space: nowrap;
    }
    code {
        word-break: break-all;
        color: ${color_shades_darkest};
    }
`;

export default TelephonyVoicePicker;
